import { useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTrialRecap, type TrialRecap } from '@/hooks/useTrialRecap';
import { PageTimeSaved } from './pages/PageTimeSaved';
import { PageMoneyTracked } from './pages/PageMoneyTracked';
import { PageGoalsSavings } from './pages/PageGoalsSavings';
import { PageFennieAI } from './pages/PageFennieAI';
import { PagePlanCompare } from './pages/PagePlanCompare';

export type RecapEntryPage =
  | 'time'
  | 'money'
  | 'goals'
  | 'fennie'
  | 'plans';

interface TrialRecapPaywallProps {
  open: boolean;
  onDismiss: () => void;
  origin: 'trial-expired' | 'premium-action';
  entryPage?: RecapEntryPage;
  lockedFeatureLabel?: string;
}

type PageKey = 'time' | 'money' | 'goals' | 'fennie' | 'plans';

function buildPages(recap: TrialRecap | null): PageKey[] {
  if (!recap) return ['plans'];
  const pages: PageKey[] = [];
  if (recap.scans.total > 0) pages.push('time');
  if (recap.money.expenseCount > 0) pages.push('money');
  if (
    recap.goals.goalsSaved > 0 ||
    recap.goals.goalsCreated > 0 ||
    recap.goals.currentStreak > 0
  )
    pages.push('goals');
  if (recap.fennie.aiInsights > 0 || recap.fennie.aiActions > 0)
    pages.push('fennie');
  pages.push('plans');
  return pages;
}

export function TrialRecapPaywall({
  open,
  onDismiss,
  origin,
  entryPage,
  lockedFeatureLabel,
}: TrialRecapPaywallProps) {
  const { data: recap, loading } = useTrialRecap(open);
  const pages = useMemo(() => buildPages(recap), [recap]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    watchDrag: true,
  });
  const [selected, setSelected] = useState(0);

  const isFinalPage = selected === pages.length - 1;

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  // Snap to entry page when it appears in the pages list
  useEffect(() => {
    if (!emblaApi || !entryPage) return;
    const idx = pages.indexOf(entryPage);
    if (idx >= 0) emblaApi.scrollTo(idx, true);
  }, [emblaApi, entryPage, pages]);

  if (!open) return null;

  const goPrev = () => emblaApi?.scrollPrev();
  const goNext = () => emblaApi?.scrollNext();

  const renderPage = (key: PageKey) => {
    switch (key) {
      case 'time':
        return <PageTimeSaved recap={recap} onContinue={goNext} />;
      case 'money':
        return <PageMoneyTracked recap={recap} onContinue={goNext} />;
      case 'goals':
        return <PageGoalsSavings recap={recap} onContinue={goNext} />;
      case 'fennie':
        return <PageFennieAI recap={recap} onContinue={goNext} />;
      case 'plans':
        return (
          <PagePlanCompare
            recap={recap}
            origin={origin}
            lockedFeatureLabel={lockedFeatureLabel}
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-background/98 backdrop-blur-md flex flex-col">
      {/* Header */}
      <div className="relative flex items-center justify-between px-4 h-14 border-b border-border/20">
        <button
          type="button"
          onClick={goPrev}
          disabled={selected === 0}
          className={cn(
            'p-2 rounded-full transition-opacity',
            selected === 0
              ? 'opacity-30 pointer-events-none'
              : 'opacity-80 hover:opacity-100',
          )}
          aria-label="Previous"
        >
          <ChevronLeft className="h-5 w-5 rtl:rotate-180" />
        </button>

        <div className="flex items-center gap-1.5">
          {pages.map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === selected
                  ? 'w-6 bg-primary'
                  : 'w-1.5 bg-muted-foreground/25',
              )}
            />
          ))}
        </div>

        {isFinalPage ? (
          <button
            type="button"
            onClick={onDismiss}
            className="p-2 -me-2 text-muted-foreground/70 hover:text-foreground text-xs uppercase tracking-widest"
            aria-label="Skip"
          >
            Skip
          </button>
        ) : (
          <span className="w-9 h-9" aria-hidden />
        )}
      </div>

      {/* Loading skeleton */}
      {loading && !recap && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-xs text-muted-foreground/60 uppercase tracking-widest">
            Preparing your recap
          </p>
        </div>
      )}

      {/* Carousel */}
      {(!loading || recap) && (
        <div className="flex-1 overflow-hidden" ref={emblaRef}>
          <div className="flex h-full">
            {pages.map((key) => (
              <div key={key} className="flex-[0_0_100%] min-w-0 h-full overflow-y-auto">
                {renderPage(key)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom next hint (hidden on final) */}
      {!isFinalPage && (
        <div className="absolute bottom-6 end-6">
          <button
            type="button"
            onClick={goNext}
            className="h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:opacity-90"
            aria-label="Next"
          >
            <ChevronRight className="h-5 w-5 rtl:rotate-180" />
          </button>
        </div>
      )}
    </div>
  );
}
